import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getUsers, deleteUser, getAssetUrl } from "../services/api";
import { ICONS } from "../icons";
import Button from "../components/UI/Button";
import SearchInput from "../components/UI/AdvancedSearchInput";
import DataTable from "../components/UI/DataTable";
import PageHeader from "../components/UI/PageHeader";
import ConfirmModal from "../components/UI/ConfirmModal"; 
import { notify } from "../utils/notify";
import { useSearchParams } from "react-router-dom"; 

export default function Users() {
  
    const [searchParams, setSearchParams] = useSearchParams();
    // أضفنا serverLimit للحالة (state) لاستلامه من الباك إيند
    const [data, setData] = useState({ items: [], total: 0, serverLimit: 10 }); 
    const [status, setStatus] = useState('idle');
    
    const currentPage = parseInt(searchParams.get("page") || "1");
    const searchTerm = searchParams.get("search") || "";
    const sortKey = searchParams.get("sort_by") || "id";
    const sortDir = searchParams.get("order") || "asc";
    
    // 💡 لم نعد نحتاج لـ const limit = 8 الثابتة هنا
  
    const [modalConfig, setModalConfig] = useState({ isOpen: false, id: null });
    const navigate = useNavigate();
    const token = localStorage.getItem("token");
  
    const fetchUsers = async () => {
      try {
        const response = await getUsers(token, {
          page: currentPage,
          search: searchTerm,
          sort_by: sortKey,
          order: sortDir,
          // لا نرسل limit من هنا لنترك الباك إيند يستخدم قيمته الافتراضية من الـ ConfigManager
        });
        
        setData({ 
          items: response.data || [], 
          total: response.meta?.total || 0,
          serverLimit: response.meta?.limit || 10 // تحديث الليميت ديناميكياً من السيرفر
        });
      } catch (err) { 
        console.error(err); 
      }
    };
  
    useEffect(() => { 
      fetchUsers(); 
    }, [currentPage, searchTerm, sortKey, sortDir]);
  
    const updateParams = (newParams) => {
      const current = Object.fromEntries(searchParams.entries());
      setSearchParams({ ...current, ...newParams });
    };
  
    const handleSort = (key) => {
      const direction = sortKey === key && sortDir === "asc" ? "desc" : "asc";
      updateParams({ sort_by: key, order: direction, page: 1 });
    };
  
    const handleSearch = (val) => {
      updateParams({ search: val, page: 1 });
    };
  
    // حساب عدد الصفحات بناءً على الليميت القادم من السيرفر
    const totalPages = Math.ceil(data.total / data.serverLimit) || 1;

    const handleDeleteClick = (id) => {
      setModalConfig({ isOpen: true, id });
    };

    const confirmDelete = async () => {
      try {
        setStatus('loading');
        await deleteUser(token, modalConfig.id);
        setStatus('success');
    
        setTimeout(() => {
            setModalConfig({ isOpen: false, id: null });
            setStatus('idle');
        }, 1500);
        notify.success("User deleted successfully");
        fetchUsers();
      } catch (err) {
        notify.error("Failed to delete user");
      }
    };

    const columns = [
      { key: "id", label: "ID", className: "text-gray-400 font-mono" },
      { 
          key: "username", 
          label: "Username", 
          render: (u) => (
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center text-[10px] font-bold text-gray-500 border border-gray-200 overflow-hidden">
                {u.avatar_url ? (
                  <img 
                    src={getAssetUrl(u.avatar_url)}
                    alt="Avatar" 
                    className="w-full h-full object-cover" 
                  />
                ) : (
                  <img 
                    src={getAssetUrl(u.gender === "female" ? "/img/avatars/Female-2.png" : "/img/avatars/Male.jpg")}
                    alt="Avatar" 
                    className="w-full h-full object-cover" 
                  />
                )}
              </div>
              <span className="font-bold text-gray-700">{u.username}</span>
            </div>
          ) 
      },
      {
        key: "full_name",
        label: "Full Name",
        className: "text-gray-600",
        render: (u) => {
          const full = [u.first_name, u.last_name].filter(Boolean).join(" ");
          return full || <span className="text-gray-300 italic">—</span>;
        },
      },
      { key: "email", label: "Email", className: "text-gray-600" },
      { 
        key: "groups", 
        label: "Groups", 
        render: (u) => (
          <div className="flex flex-wrap gap-1">
            {u.groups?.length > 0 ? u.groups.map(g => (
              <span key={g.id} className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded text-[10px] font-bold border border-gray-200">
                {g.name}
              </span>
            )) : <span className="text-gray-300 text-[10px] italic">None</span>}
          </div>
        )
      }
    ];

    return (
      <div className="p-4 md:p-6 max-w-[1600px] mx-auto transition-all duration-300">
          <ConfirmModal 
            isOpen={modalConfig.isOpen}
            title="Delete User"
            message="Are you sure? You want to remove user."
            onConfirm={confirmDelete}
            onCancel={() => setModalConfig({ isOpen: false, id: null })}
            isLoading={status === 'loading'}
            isSuccess={status === 'success'} 
            isError={status === 'error'}
          />
        
        <PageHeader className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-6 border-b border-gray-100 pb-6">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate("/dashboard")} className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400">
              <ICONS.ChevronLeft className="w-6 h-6 rtl:rotate-180" />
            </button>
            <div className="flex items-center justify-center min-w-[48px] h-[48px]" style={{ color: "var(--primary-color)" }}>
              <ICONS.Users size={40} className="shrink-0" />
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-bold" style={{ color: "var(--primary-color)" }}>Users</h2>
              <p className="text-gray-400 text-xs md:text-sm font-medium">Manage system access and roles</p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto">
            <SearchInput
              value={searchTerm}
              onChange={handleSearch}
              placeholder="Search users..."
            />
            <Button variant="primary" icon={ICONS.Plus} onClick={() => navigate("/add-user")} className="w-full sm:w-auto">
              Add User
            </Button>
          </div>
        </PageHeader>

        <DataTable 
          columns={columns}
          data={data.items}
          sortConfig={{ key: sortKey, direction: sortDir }}
          onSort={handleSort}
          renderActions={(u, isMobile) => (
            <>
              <Button variant="outline" size="sm" icon={ICONS.EditUser} onClick={() => navigate(`/edit-user/${u.id}`)}>{isMobile && " Edit"}</Button>
              <Button variant="danger" size="sm" icon={ICONS.DeleteUser} onClick={() => handleDeleteClick(u.id)}>{isMobile && " Delete"}</Button>
            </>
          )}
          mobileRender={(u) => (
            <div className="flex flex-col gap-2">
              <div className="flex justify-between items-start">
                <h3 className="font-bold text-gray-800">{u.username}</h3>
                <span className="text-[10px] text-gray-400 font-mono">#{u.id}</span>
              </div>
              <p className="text-xs text-gray-500">{u.email}</p>
              <div className="flex flex-wrap gap-1 mt-1">
                {u.groups?.map(g => (
                  <span key={g.id} className="bg-white border border-gray-200 text-gray-600 px-2 py-0.5 rounded text-[10px] font-bold">
                    {g.name}
                  </span>
                ))}
              </div>
            </div>
          )}
          paginationConfig={{
            currentPage,
            totalPages,
            onPageChange: (p) => updateParams({ page: p }),
            totalItems: data.total
          }}
        />
      </div>
    );
}